import * as Sentry from "@sentry/react";

const SENTRY_DSN = "https://115ea40f9aab4364b364f2b936621cbd@sentry.io/1724072";

const REDACTED = "[REDACTED]";

function safeStringify(value) {
	try {
		return JSON.stringify(value);
	} catch (error) {
		return "[UNSERIALIZABLE]";
	}
}

function sanitizeObject(value, depth = 0) {
	if (value === null || value === undefined) return value;
	if (depth > 4) return "[MAX_DEPTH]";

	if (Array.isArray(value)) {
		return value.slice(0, 50).map((item) => sanitizeObject(item, depth + 1));
	}

	if (typeof value !== "object") return value;

	const blockedKeys = ["authorization", "cookie", "password", "token", "access_token", "refresh_token"];
	const out = {};
	Object.keys(value).forEach((key) => {
		const lowerKey = key.toLowerCase();
		if (blockedKeys.indexOf(lowerKey) > -1) {
			out[key] = REDACTED;
			return;
		}
		out[key] = sanitizeObject(value[key], depth + 1);
	});
	return out;
}

function buildRelease() {
	const envRelease = process.env.REACT_APP_RELEASE;
	if (envRelease) return envRelease;

	const npmVersion = process.env.REACT_APP_VERSION || process.env.npm_package_version;
	return npmVersion ? `app-web@${npmVersion}` : undefined;
}

export function initSentry() {
	Sentry.init({
		dsn: process.env.REACT_APP_SENTRY_DSN || SENTRY_DSN,
		environment: process.env.REACT_APP_ENV || process.env.NODE_ENV || "development",
		release: buildRelease(),
		debug: process.env.NODE_ENV === "development",
		maxBreadcrumbs: 100,
		attachStacktrace: true,
		normalizeDepth: 6,
		ignoreErrors: [
			"Network Error",
			"timeout of 0ms exceeded",
			"Request failed with status code 401",
			"Timeout of 2000ms exceeded calling create on authentication",
			"Timeout of 5000ms exceeded calling create on authentication",
			"Non-Error promise rejection captured",
		],
		beforeSend(event) {
			if (event.request && event.request.headers) {
				event.request.headers = sanitizeObject(event.request.headers);
			}
			if (event.extra) {
				event.extra = sanitizeObject(event.extra);
			}
			if (event.user && event.user.email) {
				event.user.email = String(event.user.email).toLowerCase();
			}
			return event;
		},
	});

	Sentry.configureScope((scope) => {
		scope.setTag("app_name", "app-web");
		scope.setTag("ui_locale", navigator.language || "unknown");
		scope.setContext("runtime", {
			href: window.location && window.location.href,
			userAgent: navigator.userAgent,
			platform: navigator.platform,
		});
	});
}

export function registerSentryRouting(history) {
	if (!history || !history.listen) return;
	history.listen((location, action) => {
		const route = `${location.pathname || ""}${location.search || ""}`;
		Sentry.addBreadcrumb({
			category: "navigation",
			message: `${action || "NAVIGATE"} ${route}`,
			level: Sentry.Severity.Info,
		});
		Sentry.configureScope((scope) => {
			scope.setTag("route", route || "/");
		});
	});
}

export function setSentryUserContext(user) {
	if (!user || !user._id) {
		Sentry.setUser(null);
		return;
	}

	Sentry.setUser({
		id: user._id,
		email: user.email || undefined,
	});

	Sentry.configureScope((scope) => {
		scope.setTag("user_role_count", String(user.roles ? user.roles.length : 0));
		scope.setContext("user_meta", sanitizeObject({
			has_driver_unit: !!user.driver_unit,
			driver_groups_count: Array.isArray(user.driver_groups) ? user.driver_groups.length : 0,
			roles: user.roles,
		}));
	});
}

export function captureExceptionWithContext(error, context = {}) {
	Sentry.withScope((scope) => {
		Object.keys(context).forEach((key) => {
			const value = context[key];
			if (value && typeof value === "object") {
				scope.setContext(key, sanitizeObject(value));
			} else {
				scope.setExtra(key, value);
			}
		});

		if (error instanceof Error) {
			Sentry.captureException(error);
			return;
		}

		Sentry.captureException(new Error(typeof error === "string" ? error : safeStringify(error)));
	});
}
